import type { TExample } from '@atlas/uicorn/preview/modules';
import * as React from 'react';
import styled, { css } from 'styled-components';
import normalizeCssSize, { type TCssSize } from '@atlas/tool/box/normalizeCssSize';
import IconButton, { type TIconButtonProps } from '@atlas/uicorn/lib/Control/IconButton';
import MoonIcon from '@atlas/uicorn/lib/Icon/MoonIcon';
import SettingIcon from '@atlas/uicorn/lib/Icon/SettingsIcon';
import PersonIcon from '@atlas/uicorn/lib/Icon/PersonIcon';

const IconButtonsGroup = React.memo(function IconButtonsGroup(
    props: TIconButtonsGroupProps,
): React.ReactElement | null {
    const { gap, items, disabled, size, ...elementProps } = props;

    if (!items.length) return null;

    return (
        <SIconButtonsGroup {...elementProps} $gap={gap}>
            {items.map((item, index) => {
                if (React.isValidElement(item)) {
                    return <SGroupItem key={item.key}>{item}</SGroupItem>;
                }

                const itemProps = item as Exclude<TIconButtonsGroupItem, React.ReactElement>;

                if ('separator' in itemProps) {
                    return <SGroupSeparator key={index} />;
                }

                const { active, ...buttonProps } = itemProps;
                const buttonDisabled = disabled || buttonProps.disabled;

                return (
                    <SGroupItem key={itemProps.key ?? (itemProps.tooltip as string)}>
                        <SIconButton {...buttonProps} $active={active} size={size} disabled={buttonDisabled} />
                    </SGroupItem>
                );
            })}
        </SIconButtonsGroup>
    );
});

export default IconButtonsGroup;

// #region Styles

const SIconButton = styled(IconButton)<{ $active?: boolean }>`
    ${(p) =>
        p.$active
            ? css`
                  background-color: ${(p) => p.theme.color.grey6};
              `
            : ''}
`;

const SGroupSeparator = styled.li`
    display: flex;
    align-items: center;

    &::before {
        content: '';
        display: block;
        background: ${(p) => p.theme.color.grey6};
        width: 0.1rem;
        height: calc(100% - 1rem);
        min-height: 0.2rem;
    }
`;

const SGroupItem = styled.li`
    display: flex;
`;

const SIconButtonsGroup = styled.menu<{ $gap?: TCssSize | null }>`
    display: inline-flex;
    gap: ${(p) => normalizeCssSize(p.$gap) ?? '0.9rem'};
    margin: 0;
    padding: 0;
    list-style: none;
`;

// #endregion

// #region Types

export type TIconButtonsGroupItem =
    | (Omit<TIconButtonProps, 'key' | 'tooltip'> & { active?: boolean } & (
              | { tooltip: TIconButtonProps['tooltip']; key: React.Key }
              | { tooltip: string; key?: React.Key }
          ))
    | { separator: true }
    | React.ReactElement;

export type TIconButtonsGroupProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> & {
    items: TIconButtonsGroupItem[];
    gap?: TCssSize | null;
    size?: number;
    disabled?: boolean;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return (
            <IconButtonsGroup
                disabled={props.disabled}
                size={props.size}
                gap={props.gap}
                items={[
                    { tooltip: 'Profile', icon: <PersonIcon />, onClick: () => props.onClick('Profile') },
                    { tooltip: 'Theme', icon: <MoonIcon />, onClick: () => props.onClick('Theme') },
                    { tooltip: 'Settings', icon: <SettingIcon />, onClick: () => props.onClick('Settings') },
                ]}
            />
        );
    },
    controls: {
        disabled: 'boolean',
        size: {
            type: 'number',
            initial: 40,
            connected: false,
        },
        gap: {
            type: 'number',
            initial: 2,
            connected: false,
        },
    },
    handlers: {
        onClick: 'console',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System-2.0-%E2%80%93-Web?type=design&node-id=4-850&mode=design&t=03J7uvwFAjeVCe8Y-0',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('IconButtonsGroup', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
